import { PencilIcon, UploadIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import type { PurchaseOrderDocumentUploadBadgeConfig } from '@/configs/purchase-order-document-upload-badges';
import { PurchaseOrderDocumentDialog } from '@/features/purchase-order/purchase-order-document-dialog';
import { cn } from '@/lib/utils';
import { ProductionDeclarationStatusCode } from '@/types/core/purchase-order/production-declaration-status-code';
import type { PurchaseOrderStatusCode } from '@/types/core/purchase-order/purchase-order';

type PurchaseOrderDocumentUploadBadgeProps = {
  purchaseOrderId: string;
  purchaseOrderStatusCode: PurchaseOrderStatusCode | null;
  productionDeclarationId?: string | null;
  productionDeclarationToken?: string | null;
  supplierName?: string | null;
  productionDeclarationStatus: ProductionDeclarationStatusCode | null;
  isConcernedByProductionDeclaration: boolean;
  initialRecipientEmails: string[];
  initialRecipientLabels: Record<string, string>;
  badgeConfig: PurchaseOrderDocumentUploadBadgeConfig;
};

export function PurchaseOrderDocumentUploadBadge({
  purchaseOrderId,
  purchaseOrderStatusCode,
  productionDeclarationId,
  productionDeclarationToken,
  supplierName,
  productionDeclarationStatus,
  isConcernedByProductionDeclaration,
  initialRecipientEmails,
  initialRecipientLabels,
  badgeConfig,
}: PurchaseOrderDocumentUploadBadgeProps) {
  const { t } = useTranslation('purchaseOrder');
  const [dialogOpen, setDialogOpen] = useState(false);
  const includeMailFields =
    badgeConfig.includeMailFields ||
    (badgeConfig.includeMailFieldsWhenConcernedByProductionDeclaration &&
      isConcernedByProductionDeclaration);
  const canUploadForStatus =
    !!purchaseOrderStatusCode &&
    badgeConfig.allowedStatusCodes.includes(purchaseOrderStatusCode);
  const canUploadForProductionDeclaration =
    !badgeConfig.requiresApprovedProductionDeclaration ||
    productionDeclarationStatus === ProductionDeclarationStatusCode.Approved;
  const canUpload = canUploadForStatus && canUploadForProductionDeclaration;
  const nextStatusCode = purchaseOrderStatusCode
    ? badgeConfig.nextStatusByCurrentStatus?.[purchaseOrderStatusCode]
    : undefined;
  const dialogDescription =
    purchaseOrderStatusCode && nextStatusCode
      ? t('documentUploadBadges.statusChangeDescription', {
          fromStatus: t(`status.${purchaseOrderStatusCode}`),
          toStatus: t(`status.${nextStatusCode}`),
        })
      : undefined;
  const dialogTitleKey =
    (isConcernedByProductionDeclaration &&
      badgeConfig.dialogTitleWhenConcernedByProductionDeclarationKey) ||
    badgeConfig.dialogTitleKey;
  const dialogTitle = dialogTitleKey ? t(dialogTitleKey) : undefined;
  const hasExistingDocument =
    !!purchaseOrderStatusCode &&
    !!badgeConfig.initialUploadStatusCode &&
    purchaseOrderStatusCode !== badgeConfig.initialUploadStatusCode;
  const visualConfig =
    (hasExistingDocument && badgeConfig.existingDocumentVisual) ||
    badgeConfig.visual;
  const Icon = visualConfig.icon === 'pencil' ? PencilIcon : UploadIcon;

  if (!canUpload) {
    return null;
  }

  return (
    <>
      <Badge
        asChild
        className='cursor-pointer rounded-full px-3 py-1 text-sm'
        variant={visualConfig.variant}
      >
        <button onClick={() => setDialogOpen(true)} type='button'>
          <span
            aria-hidden
            className={cn('size-2 rounded-full', visualConfig.dotColorClass)}
          />
          {t(badgeConfig.labelKey)}
          <Icon aria-hidden className='size-3.5' />
        </button>
      </Badge>
      <PurchaseOrderDocumentDialog
        description={dialogDescription}
        fixedDocumentTypeCode={badgeConfig.documentTypeCode}
        includeMailFields={includeMailFields}
        initialRecipientEmails={initialRecipientEmails}
        initialRecipientLabels={initialRecipientLabels}
        isConcernedByProductionDeclaration={isConcernedByProductionDeclaration}
        onOpenChange={setDialogOpen}
        open={dialogOpen}
        productionDeclarationId={productionDeclarationId}
        productionDeclarationToken={productionDeclarationToken}
        purchaseOrderId={purchaseOrderId}
        supplierName={supplierName}
        title={dialogTitle}
      />
    </>
  );
}
